import { getMerchantId, ReparoApi } from './index';

import type { AlertArtifactLink } from '@components/TodoChat/types';

/**
 * Scheduled alerts created from a surface of the product ("schedule this plan", "watch these
 * orders"): one call that stores the alert type, the inputs the evaluator needs (`context`)
 * and the deep link back to the surface (`context.artifact`). The evaluator re-runs the
 * registered service method on the schedule and the Home card opens the link.
 */
export type ScheduledAlertFrequency = 'EVERY_6H' | 'EVERY_12H' | 'DAILY' | 'WEEKLY';

export interface ScheduleAlertSchedule {
    frequency: ScheduledAlertFrequency;
    /** HH:mm in the brand's schedule timezone. */
    triggerTime: string;
    /** 0 = Sunday … 6 = Saturday; WEEKLY only. */
    triggerDayOfWeek?: number | null;
}

export interface CreateScheduledAlertInput extends ScheduleAlertSchedule {
    alertType: string;
    alertLabel: string;
    /** Everything the alert's source method needs, plus `artifact` for the card's button. */
    context: Record<string, unknown> & { artifact?: AlertArtifactLink };
}

export interface UserAlertRow {
    alertId: string;
    alertType: string;
    alertLabel: string | null;
    context: string | null;
    frequency: string | null;
    triggerTime: string | null;
    triggerDayOfWeek: number | null;
    isScheduled: boolean;
    lastEvaluatedAt: string | null;
}

// The user-alert routes take the merchant explicitly (body / query) rather than from the JWT.
export const createScheduledAlert = async (input: CreateScheduledAlertInput): Promise<UserAlertRow> =>
    ReparoApi<UserAlertRow>({
        method: 'POST',
        url: '/user-alerts',
        body: JSON.stringify({
            merchant_id: await getMerchantId(),
            alertType: input.alertType,
            alertLabel: input.alertLabel,
            context: input.context,
            frequency: input.frequency,
            triggerTime: input.triggerTime,
            ...(input.frequency === 'WEEKLY' ? { triggerDayOfWeek: input.triggerDayOfWeek ?? 1 } : {}),
            isScheduled: true,
        }),
    });

export const listUserAlerts = async (): Promise<UserAlertRow[]> =>
    ReparoApi<UserAlertRow[]>({ method: 'GET', url: '/user-alerts', body: null });

/** Removes the alert definition; its schedule stops and its open cards go with it. */
export const deleteUserAlert = async (alertId: string): Promise<void> => {
    const merchantId = await getMerchantId();
    await ReparoApi<unknown>({
        method: 'DELETE',
        url: `/user-alerts/${encodeURIComponent(alertId)}?merchant_id=${encodeURIComponent(merchantId ?? '')}`,
        body: null,
    });
};

/**
 * Parsed `context` of the alerts already scheduled against a given artifact route, so a
 * surface can show "scheduled daily at 07:00" instead of a second Schedule button.
 */
export const findScheduledAlerts = async (
    match: (context: Record<string, unknown>) => boolean,
): Promise<Array<UserAlertRow & { parsedContext: Record<string, unknown> }>> => {
    const rows = await listUserAlerts();
    const out: Array<UserAlertRow & { parsedContext: Record<string, unknown> }> = [];
    for (const r of rows) {
        if (!r.isScheduled || !r.context) continue;
        try {
            const parsed = JSON.parse(r.context) as Record<string, unknown>;
            if (match(parsed)) out.push({ ...r, parsedContext: parsed });
        } catch {
            /* unreadable context: not ours */
        }
    }
    return out;
};
